import { useParams } from "react-router-dom";
import React, { useState, useEffect, forwardRef } from 'react';
import { Sidebar } from '../dashboard_component';
import { ToastContainer, toast } from 'react-toastify';
import QRCode from "react-qr-code";
import { cbtAPI } from '../../api';
import 'react-toastify/dist/ReactToastify.css';

const TestData = () => {
  const [attempts, setAttempts] = useState([]);
  const [testName, setTestName] = useState('');
  const [studentId, setStudentId] = useState('');
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { cbt_pass_code, user_id } = useParams();

  const fetchTestData = async () => {
    if (!user_id) {
      setIsLoading(false);
      toast.error("Student ID is missing");
      return;
    }

    setIsLoading(true);
    try {
      const response = await cbtAPI.getStudentResult(cbt_pass_code, user_id);
      const data = response.data;
      setTestName(data.cbt_name || '');
      setStudentId(data.student_email || '');
      setTotalAttempts(data.total_attempts || 0);
      setAttempts(data.attempts || []);
      toast.success('Test data loaded');
    } catch (error) {
      console.error('Error fetching test data:', error);
      toast.error('Failed to load test data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTestData();
  }, [cbt_pass_code, user_id]);

  const handleToggle = () => setIsCollapsed(!isCollapsed);

  const cbtbarcode = `${window.location.origin}/qr_verification?cbt_pass_code=${cbt_pass_code}`;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/20">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
        isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {testName ? `${testName} - Detailed Analysis` : 'Test Analysis'}
                </h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {cbt_pass_code}
                  </span>
                  <span className="text-gray-600">•</span>
                  <span className="text-gray-600 font-medium">Student: {studentId}</span>
                  <span className="text-gray-600">•</span>
                  <span className="text-gray-600">Total Attempts: {totalAttempts}</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
                  <QRCode size={80} value={cbtbarcode} />
                </div>
                <button
                  onClick={fetchTestData}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Attempts List */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading attempts...</p>
            </div>
          ) : attempts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
              <p className="text-gray-500">No attempts found for this student.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {attempts.map((attempt) => (
                <AttemptCard
                  key={attempt.session_id}
                  attempt={attempt}
                  cbt_pass_code={cbt_pass_code}
                  student_id={user_id}
                  refresh={fetchTestData}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        toastClassName="rounded-xl shadow-lg"
        progressClassName="bg-gradient-to-r from-blue-500 to-purple-500"
      />
    </div>
  );
};

const AttemptCard = ({ attempt, cbt_pass_code, student_id, refresh }) => {
  const [expanded, setExpanded] = useState(false);

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const d = new Date(isoString);
    return d.toLocaleString();
  };

  const statusColor = attempt.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Summary Bar */}
      <div
        className={`p-5 flex flex-col sm:flex-row sm:items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${
          expanded ? 'border-b border-gray-200' : ''
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold">
            #{attempt.attempt_number}
          </span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">Session:</span>
              <span className="font-mono text-sm text-gray-600">{attempt.session_id.substring(0, 12)}...</span>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span>{formatDateTime(attempt.start_time)}</span>
              <span>•</span>
              <span>{attempt.time_spent}s</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2 sm:mt-0">
          <div className="text-right">
            <div className="font-bold text-lg">{attempt.total_score.toFixed(1)}</div>
            <div className="text-sm text-gray-500">{attempt.percentage.toFixed(1)}%</div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor}`}>
            {attempt.grade || (attempt.passed ? 'Pass' : 'Fail')}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-gray-500">Test ID</span>
              <div className="font-mono font-medium">{attempt.test_id.substring(0, 12)}...</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-gray-500">Status</span>
              <div className="font-medium capitalize">{attempt.status}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-gray-500">Time Spent</span>
              <div className="font-medium">{attempt.time_spent} sec</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-gray-500">Questions</span>
              <div className="font-medium">{attempt.answers_review?.length || 0}</div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-lg">Questions & Answers</h3>
            {attempt.answers_review?.map((answer, idx) => (
              <QuestionReview
                key={answer.question_id}
                answer={answer}
                index={idx}
                cbt_pass_code={cbt_pass_code}
                cbt_test_id={attempt.test_id}
                student_id={student_id}
                session_id={attempt.session_id}
                refresh={refresh}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionReview = ({ answer, index, cbt_pass_code, cbt_test_id, student_id, session_id, refresh }) => {
  const [customScore, setCustomScore] = useState(answer.score_obtained || '');
  const [isSavingScore, setIsSavingScore] = useState(false);

  const submitCustomScore = async (e) => {
    e.preventDefault();
    setIsSavingScore(true);
    try {
      await cbtAPI.updateScore({
        cbt_question_id: answer.question_id,
        custom_score: customScore,
        cbt_pass_code,
        cbt_test_id,
        student_id,
        session_id,
      });
      toast.success(`✅ Score updated to ${customScore}`);
      if (refresh) refresh();
    } catch (error) {
      console.error('Score update error:', error);
      toast.error(error.data?.detail || 'Failed to update score');
    } finally {
      setIsSavingScore(false);
    }
  };

  const getScoreColor = (score) => {
    const num = parseFloat(score) || 0;
    const max = parseFloat(answer.max_score) || 1;
    const percentage = (num / max) * 100;
    if (percentage >= 80) return "bg-green-100 text-green-800 border-green-200";
    if (percentage >= 60) return "bg-blue-100 text-blue-800 border-blue-200";
    if (percentage >= 40) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-red-100 text-red-800 border-red-200";
  };

  const currentScore = parseFloat(answer.score_obtained) || 0;
  const maxScore = parseFloat(answer.max_score) || 1;
  const isEssay = answer.question_type === 'essay';

  return (
    <div className="border rounded-xl p-5 space-y-4 bg-gray-50/30">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h4 className="font-semibold text-gray-800">Question {index + 1}</h4>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getScoreColor(currentScore)}`}>
          Score: {currentScore.toFixed(2)} / {maxScore.toFixed(2)}
        </span>
      </div>

      {/* Question text as HTML */}
      <div
        className="prose prose-sm max-w-none bg-white p-4 rounded-lg border border-gray-200"
        dangerouslySetInnerHTML={{ __html: answer.question_text || 'Question text not available' }}
      />

      {/* Student's Answer */}
      <div>
        <p className="font-medium text-gray-700 mb-2">Student's Answer:</p>
        {isEssay ? (
          // Essay answer is a string (HTML)
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div dangerouslySetInnerHTML={{ __html: answer.student_answer || '<em>No answer provided</em>' }} />
          </div>
        ) : (
          // Multiple choice: student_answer is an array of selected option IDs
          <>
            {answer.student_answer && answer.student_answer.length > 0 ? (
              <div className="space-y-2">
                {answer.options
                  ?.filter(opt => answer.student_answer.includes(opt.id))
                  .map(opt => (
                    <div
                      key={opt.id}
                      className={`p-3 rounded-lg border ${
                        opt.is_correct ? 'bg-green-100 border-green-300' : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div dangerouslySetInnerHTML={{ __html: opt.text }} />
                      {opt.is_correct && <span className="text-xs text-green-700 ml-2">(Correct)</span>}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="bg-gray-100 p-3 rounded-lg text-gray-500 italic">No answer selected</div>
            )}
          </>
        )}
      </div>

      {/* All options (only for multiple choice) */}
      {!isEssay && answer.options && answer.options.length > 0 && (
        <div>
          <p className="font-medium text-gray-700 mb-2">All Options:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {answer.options.map(opt => {
              const isSelected = answer.student_answer?.includes(opt.id);
              return (
                <div
                  key={opt.id}
                  className={`p-2 rounded border ${
                    opt.is_correct ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                  } ${isSelected ? 'ring-2 ring-blue-400' : ''}`}
                >
                  <div dangerouslySetInnerHTML={{ __html: opt.text }} />
                  {opt.is_correct && <span className="text-xs text-green-600 ml-2">(Correct)</span>}
                  {isSelected && !opt.is_correct && (
                    <span className="text-xs text-red-600 ml-2">(Selected but incorrect)</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Essay correct answer (if available) */}
      {isEssay && answer.correct_answer && (
        <div>
          <p className="font-medium text-gray-700 mb-2">Model Answer:</p>
          <div
            className="bg-green-50 p-4 rounded-lg border border-green-200"
            dangerouslySetInnerHTML={{ __html: answer.correct_answer }}
          />
        </div>
      )}

      {/* Custom grading form (works for both types) */}
      <form onSubmit={submitCustomScore} className="bg-white p-4 rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Adjust Score</label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="number"
            min="0"
            step="0.5"
            max={maxScore}
            value={customScore}
            onChange={(e) => setCustomScore(e.target.value)}
            className="w-28 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Score"
          />
          <span className="text-gray-500">points (max {maxScore.toFixed(2)})</span>
          <button
            type="submit"
            disabled={isSavingScore}
            className={`px-4 py-2 rounded-lg font-medium text-white ${
              isSavingScore ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
            } transition-colors`}
          >
            {isSavingScore ? 'Updating...' : 'Update'}
          </button>
          <button
            type="button"
            onClick={() => setCustomScore(answer.score_obtained || '')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

// const QuestionReview = ({ answer, index, cbt_pass_code, cbt_test_id, student_id, session_id, refresh }) => {
//   const [customScore, setCustomScore] = useState(answer.score_obtained || '');
//   const [isSavingScore, setIsSavingScore] = useState(false);

//   const submitCustomScore = async (e) => {
//     e.preventDefault();
//     setIsSavingScore(true);
//     try {
//       // Adjust the payload to match your backend endpoint
//       await cbtAPI.updateScore({
//         cbt_question_id: answer.question_id,
//         custom_score: customScore,
//         cbt_pass_code,
//         cbt_test_id,
//         student_id,
//         session_id, // optional, for extra safety
//       });
//       toast.success(`✅ Score updated to ${customScore}`);
//       if (refresh) refresh();
//     } catch (error) {
//       console.error('Score update error:', error);
//       toast.error(error.data?.detail || 'Failed to update score');
//     } finally {
//       setIsSavingScore(false);
//     }
//   };

//   const getScoreColor = (score) => {
//     const num = parseFloat(score) || 0;
//     const max = parseFloat(answer.max_score) || 1;
//     const percentage = (num / max) * 100;
//     if (percentage >= 80) return "bg-green-100 text-green-800 border-green-200";
//     if (percentage >= 60) return "bg-blue-100 text-blue-800 border-blue-200";
//     if (percentage >= 40) return "bg-yellow-100 text-yellow-800 border-yellow-200";
//     return "bg-red-100 text-red-800 border-red-200";
//   };

//   const currentScore = parseFloat(answer.score_obtained) || 0;
//   const maxScore = parseFloat(answer.max_score) || 1;

//   // Find selected option(s) text
//   const selectedOptionIds = answer.student_answer || [];
//   const selectedOptions = answer.options?.filter(opt => selectedOptionIds.includes(opt.id)) || [];

//   return (
//     <div className="border rounded-xl p-5 space-y-4 bg-gray-50/30">
//       <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
//         <h4 className="font-semibold text-gray-800">Question {index + 1}</h4>
//         <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getScoreColor(currentScore)}`}>
//           Score: {currentScore.toFixed(2)} / {maxScore.toFixed(2)}
//         </span>
//       </div>

//       {/* Question text as HTML */}
//       <div
//         className="prose prose-sm max-w-none bg-white p-4 rounded-lg border border-gray-200"
//         dangerouslySetInnerHTML={{ __html: answer.question_text || 'Question text not available' }}
//       />

//       {/* Student's answer */}
//       <div>
//         <p className="font-medium text-gray-700 mb-2">Student's Answer:</p>
//         {selectedOptions.length > 0 ? (
//           <div className="space-y-2">
//             {selectedOptions.map(opt => (
//               <div
//                 key={opt.id}
//                 className={`p-3 rounded-lg border ${
//                   opt.is_correct ? 'bg-green-100 border-green-300' : 'bg-blue-50 border-blue-200'
//                 }`}
//               >
//                 <div dangerouslySetInnerHTML={{ __html: opt.text }} />
//                 {opt.is_correct && <span className="text-xs text-green-700 ml-2">(Correct)</span>}
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div className="bg-gray-100 p-3 rounded-lg text-gray-500 italic">No answer provided</div>
//         )}
//       </div>

//       {/* All options for reference */}
//       {answer.options && answer.options.length > 0 && (
//         <div>
//           <p className="font-medium text-gray-700 mb-2">All Options:</p>
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
//             {answer.options.map(opt => (
//               <div
//                 key={opt.id}
//                 className={`p-2 rounded border ${
//                   opt.is_correct ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
//                 } ${selectedOptionIds.includes(opt.id) ? 'ring-2 ring-blue-400' : ''}`}
//               >
//                 <div dangerouslySetInnerHTML={{ __html: opt.text }} />
//                 {opt.is_correct && <span className="text-xs text-green-600 ml-2">(Correct)</span>}
//                 {selectedOptionIds.includes(opt.id) && !opt.is_correct && (
//                   <span className="text-xs text-red-600 ml-2">(Selected but incorrect)</span>
//                 )}
//               </div>
//             ))}
//           </div>
//         </div>
//       )}

//       {/* Custom grading form */}
//       <form onSubmit={submitCustomScore} className="bg-white p-4 rounded-lg border border-gray-200">
//         <label className="block text-sm font-medium text-gray-700 mb-2">Adjust Score</label>
//         <div className="flex flex-wrap items-center gap-3">
//           <input
//             type="number"
//             min="0"
//             step="0.5"
//             max={maxScore}
//             value={customScore}
//             onChange={(e) => setCustomScore(e.target.value)}
//             className="w-28 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//             placeholder="Score"
//           />
//           <span className="text-gray-500">points (max {maxScore.toFixed(2)})</span>
//           <button
//             type="submit"
//             disabled={isSavingScore}
//             className={`px-4 py-2 rounded-lg font-medium text-white ${
//               isSavingScore ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
//             } transition-colors`}
//           >
//             {isSavingScore ? 'Updating...' : 'Update'}
//           </button>
//           <button
//             type="button"
//             onClick={() => setCustomScore(answer.score_obtained || '')}
//             className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
//           >
//             Reset
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

export { TestData };